import consts from '../constant';

const BINANCE_REST_BASE_URLS = [
    'https://data-api.binance.vision',
    'https://api.binance.com',
    'https://api1.binance.com',
    'https://api2.binance.com',
    'https://api3.binance.com',
    'https://api.binance.us'
];
const BINANCE_KLINE_LIMIT = 1000;
const MIN_PRICE = 0.00000001;
const REQUEST_TIMEOUT_MS = 2500;

async function fetchWithTimeout(url, { signal, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => {
        try { controller.abort(); } catch (_) {}
    }, timeoutMs);

    if (signal) {
        if (signal.aborted) {
            controller.abort();
        } else {
            signal.addEventListener('abort', () => {
                try { controller.abort(); } catch (_) {}
            }, { once: true });
        }
    }

    try {
        return await fetch(url, { signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

export async function fetchHistoricalTickData({ symbol, points = 3600, interval = '1s', signal } = {}) {
    if (!symbol || !Number.isFinite(points) || points <= 0) {
        return [];
    }
    const normalizedSymbol = String(symbol).toUpperCase();
    const normalizedInterval = typeof interval === 'string' && interval.trim().length > 0 ? interval.trim() : '1s';
    const batches = [];
    let remaining = Math.max(1, Math.floor(points));
    let endTimeMs = Date.now();
    let requests = 0;
    while (remaining > 0 && requests < 8) {
        const limit = Math.min(BINANCE_KLINE_LIMIT, remaining);
        const payload = await fetchKlineBatch({
            symbol: normalizedSymbol,
            interval: normalizedInterval,
            limit,
            endTimeMs,
            signal
        });
        if (!Array.isArray(payload) || payload.length === 0) {
            break;
        }
        const parsedBatch = payload
            .map(toHistoryPoint)
            .filter((point) => point !== null);
        if (parsedBatch.length === 0) {
            break;
        }
        batches.unshift(parsedBatch);
        remaining -= parsedBatch.length;
        requests += 1;
        const oldestOpenMs = Number(payload[0]?.[0]);
        if (!Number.isFinite(oldestOpenMs) || oldestOpenMs <= 0 || payload.length < limit) {
            break;
        }
        endTimeMs = oldestOpenMs - 1;
    }
    return dedupeBySecond(batches.flat()).slice(-Math.floor(points));
}

async function fetchKlineBatch({ symbol, interval, limit, endTimeMs, signal }) {
    let lastError = null;

    // 1. Primary: Try our VPS Backend Proxy first (bypasses all ISP blocks globally)
    if (consts?.BackendUrl) {
        try {
            const proxyUrl = new URL(`${consts.BackendUrl}/binance/klines`);
            proxyUrl.searchParams.set('symbol', symbol);
            proxyUrl.searchParams.set('interval', interval);
            proxyUrl.searchParams.set('limit', String(limit));
            if (Number.isFinite(endTimeMs) && endTimeMs > 0) {
                proxyUrl.searchParams.set('endTime', String(endTimeMs));
            }
            const response = await fetchWithTimeout(proxyUrl.toString(), { signal, timeoutMs: 5000 });
            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data) && data.length > 0) {
                    return data;
                }
            }
        } catch (error) {
            lastError = error;
        }
    }

    // 2. Fallback: Try direct Binance endpoints with strict 2s timeout per endpoint
    for (const baseUrl of BINANCE_REST_BASE_URLS) {
        try {
            const url = new URL('/api/v3/klines', baseUrl);
            url.searchParams.set('symbol', symbol);
            url.searchParams.set('interval', interval);
            url.searchParams.set('limit', String(limit));
            url.searchParams.set('endTime', String(endTimeMs));
            const response = await fetchWithTimeout(url.toString(), { signal, timeoutMs: 2000 });
            if (!response.ok) {
                lastError = new Error(`Unable to fetch Binance history (${response.status}) from ${baseUrl}`);
                continue;
            }
            return await response.json();
        }
        catch (error) {
            lastError = error;
        }
    }
    throw lastError ?? new Error('Unable to fetch Binance history');
}

function toHistoryPoint(rawKline) {
    const openTimeMs = Number(rawKline?.[0]);
    const openPrice = Number(rawKline?.[1]);
    const highPrice = Number(rawKline?.[2]);
    const lowPrice = Number(rawKline?.[3]);
    const closePrice = Number(rawKline?.[4]);
    if (!Number.isFinite(openTimeMs) ||
        !Number.isFinite(openPrice) ||
        !Number.isFinite(highPrice) ||
        !Number.isFinite(lowPrice) ||
        !Number.isFinite(closePrice)) {
        return null;
    }
    const normalizedOpen = Math.max(MIN_PRICE, openPrice);
    const normalizedHigh = Math.max(MIN_PRICE, highPrice);
    const normalizedLow = Math.max(MIN_PRICE, lowPrice);
    const normalizedClose = Math.max(MIN_PRICE, closePrice);
    return {
        time: Math.floor(openTimeMs / 1000),
        value: normalizedClose,
        open: normalizedOpen,
        high: Math.max(normalizedHigh, normalizedOpen, normalizedClose),
        low: Math.min(normalizedLow, normalizedOpen, normalizedClose),
        close: normalizedClose
    };
}

function dedupeBySecond(points) {
    if (points.length === 0) {
        return [];
    }
    const byTime = new Map();
    for (const point of points) {
        if (!Number.isFinite(point.time) || !Number.isFinite(point.value)) {
            continue;
        }
        byTime.set(point.time, point);
    }
    return Array.from(byTime.values()).sort((left, right) => left.time - right.time);
}
